/**
 * Sending a report to `/report` (REPORT-SPEC §5.4, §7.2, §8.3).
 *
 * ## Nothing about this is fire-and-forget, unlike `/prepare`
 *
 * The share-card ask swallows every failure on purpose: a card is a nicety and
 * its absence is invisible to the page. This is the opposite. Somebody has just
 * written out what is wrong with a model, by hand, and pressed a button. They
 * are owed an answer either way — so every outcome maps to something the dialog
 * can say, and none of them is silence.
 *
 * That includes the secure-context failure. `crypto.subtle` needs HTTPS, and on
 * a plain-HTTP dev server the hash throws; `/prepare` treats that as correct
 * behaviour and stops. Here it becomes a visible failure, because a developer
 * testing the dialog needs to know why nothing happened.
 */

import { bodyHash } from "../util/body-hash.js";
import type { AcceptedReport } from "./contract.js";

/** §8.3's outcomes, as things the dialog can act on rather than status codes. */
export type SubmitResult =
  | { readonly kind: "sent"; readonly reportId: string }
  /** 400. Our own request was malformed — a client defect, not the visitor's. */
  | { readonly kind: "invalid"; readonly field?: string }
  /** 409. §5.4: a new submission id and one retry. */
  | { readonly kind: "conflict" }
  | { readonly kind: "rate-limited" }
  | { readonly kind: "too-large" }
  | { readonly kind: "failed" };

const ENDPOINT = "/report";

/**
 * One attempt. The caller owns retrying, because it owns the submission id.
 *
 * Deliberately takes the already-assembled report rather than the form's state:
 * §8.4's digest is over exactly these fields, so a retry must send exactly the
 * same bytes. Rebuilding the body here from live form state would let a
 * keystroke between attempts turn a replay into a 409.
 */
export async function submitReport(report: AcceptedReport): Promise<SubmitResult> {
  const body = JSON.stringify(report);

  let response: Response;
  try {
    response = await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        // The OAC signature's payload hash. Without it the edge answers 403
        // before the function is invoked — see `util/body-hash.ts`.
        "x-amz-content-sha256": await bodyHash(body),
      },
      body,
      /*
       * No credentials, ever. There are none to send, and asking for them would
       * make this a request that *could* carry identity — which is the thing
       * the whole design is arranged to avoid.
       */
      credentials: "omit",
      /*
       * Deliberately **not** `keepalive`, unlike `/prepare`.
       *
       * That flag lets a request outlive the page, which is right for a
       * fire-and-forget ask nobody waits on. Here the visitor is watching for a
       * receipt, so the request must live exactly as long as the dialog does —
       * and `keepalive` caps the body at 64 KiB anyway, a limit this would
       * silently inherit for no benefit.
       */
    });
  } catch {
    // Offline, blocked by an extension, DNS, or `crypto.subtle` on an insecure
    // origin. All of them are "it did not reach us", which is what the dialog
    // says.
    return { kind: "failed" };
  }

  if (response.status === 201 || response.status === 200) {
    try {
      const payload = (await response.json()) as { reportId?: unknown };
      if (typeof payload.reportId === "string") {
        return { kind: "sent", reportId: payload.reportId };
      }
    } catch {
      // A 2xx whose body will not parse. The report was almost certainly
      // stored, and claiming otherwise would invite a duplicate — but there is
      // no receipt to show, so this reports success without one.
    }
    return { kind: "sent", reportId: "" };
  }

  switch (response.status) {
    case 400:
      return { kind: "invalid", field: await safeField(response) };
    case 409:
      return { kind: "conflict" };
    case 413:
      return { kind: "too-large" };
    case 429:
      return { kind: "rate-limited" };
    default:
      return { kind: "failed" };
  }
}

/**
 * The field name from a 400, if there is one.
 *
 * §8.3's error body carries a field name and nothing else, and it is one of a
 * fixed set the server chooses — never text a visitor supplied. It is still
 * treated as untrusted here: read defensively, bounded, and the dialog sets it
 * with `textContent`.
 *
 * A 400 reaching a real visitor means **our** request was malformed, since the
 * form cannot produce most of these states. It is worth showing because it
 * tells a developer which field to look at.
 */
async function safeField(response: Response): Promise<string | undefined> {
  try {
    const payload = (await response.json()) as { field?: unknown };
    return typeof payload.field === "string" && payload.field.length <= 64
      ? payload.field
      : undefined;
  } catch {
    return undefined;
  }
}
